import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Mail } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { getSdgTagStyle, SDG_TILES } from '../constants/sdgTags';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_STATS = [
  { value: '42', label: 'Portfolio companies' },
  { value: '199', label: 'Patents filed' },
  { value: '3,380', label: 'Jobs created' },
];

const HERO_FEATURE_CARDS = [
  {
    eyebrow: 'Signatory since 2026',
    title: 'UN Principles for Responsible Investment',
    subtitle: 'United Nations-backed global ESG framework',
    href: 'https://www.unpri.org/',
    ariaLabel: 'UN Principles for Responsible Investment (opens in new tab)',
    icon: '/images/esg/un-pri.png',
    iconAlt: 'UN Principles for Responsible Investment',
  },
  {
    eyebrow: 'We are gender aligned',
    title: '2X Challenge',
    subtitle: 'Movement launched by G7 Summit in Canada',
    href: 'https://2xchallenge.org/',
    ariaLabel: '2X Challenge (opens in new tab)',
    icon: '/images/esg/gender-2x.png',
    iconAlt: '2X Challenge gender alignment',
  },
];

const ESG_PILLARS = [
  {
    title: 'Environmental',
    description: 'Building a greener tomorrow',
    icon: '/images/esg/environmental.png',
    iconAlt: 'Environmental icon',
  },
  {
    title: 'Social',
    description: 'Inclusion at every level',
    icon: '/images/esg/social.png',
    iconAlt: 'Social icon',
  },
  {
    title: 'Governance',
    description: 'Transparent and ethical practices',
    icon: '/images/esg/governance.png',
    iconAlt: 'Governance icon',
  },
];

const FRAMEWORK_PHASES = [
  {
    phase: 'Phase 1',
    title: 'Evaluation & screening',
    bullets: [
      'ESG risk screening at initial evaluation stage',
      'Sector-specific exclusion criteria aligned with our policy',
      'Alignment assessment against YourNest ESG commitments',
    ],
  },
  {
    phase: 'Phase 2',
    title: 'Diligence & alignment',
    bullets: [
      'ESG due diligence questionnaires and management assessment',
      'SDG alignment mapping for each investment opportunity',
      'Integration of ESG findings into investment committee decisions',
    ],
  },
  {
    phase: 'Phase 3',
    title: 'Active stewardship & monitoring',
    bullets: [
      'Ongoing portfolio company ESG monitoring and reporting',
      'Board-level guidance on governance and sustainability practices',
      'Annual ESG disclosures and impact measurement across the portfolio',
    ],
  },
];

const UN_PRI_PRINCIPLES = [
  'We will incorporate ESG issues into investment analysis and decision-making processes.',
  'We will be active owners and incorporate ESG issues into our ownership policies and practices.',
  'We will seek appropriate disclosure on ESG issues by the entities in which we invest.',
  'We will promote acceptance and implementation of the Principles within the investment industry.',
  'We will work together to enhance our effectiveness in implementing the Principles.',
  'We will each report on our activities and progress towards implementing the Principles.',
];

const PORTFOLIO_SPOTLIGHT = [
  {
    name: 'Miko',
    portfolioId: 'Miko',
    description:
      'Empowers children aged 4-8 with an emotionally intelligent AI companion that educates and entertains. Loved by kids in 140+ countries, its products — Miko Mini and Miko Chess — combine learning, play, and innovation through engaging subscription-based content.',
    sdgTags: ['Quality Education', 'Industry & Innovation'],
  },
  {
    name: 'Dozee',
    portfolioId: 'Dozee',
    description:
      'India\'s first contactless remote health monitoring platform, improving access to quality healthcare through AI-powered vital tracking.',
    sdgTags: ['Good Health & Wellbeing', 'Industry & Innovation'],
  },
  {
    name: 'Exponent Energy',
    portfolioId: 'Exponent',
    description:
      'Accelerating India\'s EV transition with 15-minute rapid-charge battery packs, reducing range anxiety and enabling cleaner urban mobility.',
    sdgTags: ['Affordable & Clean Energy', 'Climate Action'],
  },
  {
    name: 'Wiom',
    portfolioId: 'Wiom',
    description:
      'India\'s first and largest PDOA, democratising affordable internet access and bridging the digital divide for underserved communities.',
    sdgTags: ['Reduced Inequality', 'Quality Education'],
  },
  {
    name: 'Aliste',
    portfolioId: 'Aliste',
    description:
      'Smart home automation solutions enabling energy-efficient living and responsible consumption through intelligent device management.',
    sdgTags: ['Responsible Consumption', 'Affordable & Clean Energy'],
  },
  {
    name: 'MapMyCrop',
    portfolioId: 'MapMyCrop',
    description:
      'AI/ML-powered satellite imagery platform optimising crop yields, soil health, and sustainable agricultural practices for farmers.',
    sdgTags: ['Zero Hunger', 'Climate Action'],
  },
];

const HeroFeatureCard = ({ card }) => (
  <a
    href={card.href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-4 bg-[#FAFAFA] rounded-2xl border border-gray-100 p-5 md:p-6 hover:border-[#C8473A]/30 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8473A] focus-visible:ring-offset-2"
    aria-label={card.ariaLabel}
  >
    <img
      src={card.icon}
      alt=""
      aria-hidden="true"
      className="w-14 h-14 md:w-16 md:h-16 flex-shrink-0 object-contain"
    />
    <div className="min-w-0">
      <p className="text-xs font-semibold text-[#C8473A] uppercase tracking-wider mb-1">
        {card.eyebrow}
      </p>
      <p className="text-base md:text-lg font-bold text-[#211F1F] leading-snug">{card.title}</p>
      <p className="text-sm text-gray-500 mt-1">{card.subtitle}</p>
    </div>
  </a>
);

const SdgTag = ({ tag }) => (
  <span
    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
    style={getSdgTagStyle(tag)}
  >
    {tag}
  </span>
);

const SdgTile = ({ tile, className = '' }) => (
  <a
    href="https://sdgs.un.org/goals"
    target="_blank"
    rel="noopener noreferrer"
    className={`block aspect-square rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8473A] focus-visible:ring-offset-2 ${className}`}
    aria-label={`${tile.alt} (opens in new tab)`}
  >
    <img
      src={tile.src}
      alt={tile.alt}
      className="w-full h-full object-cover"
      loading="lazy"
    />
  </a>
);

const EsgPage = () => {
  usePageTitle('ESG');
  const [portfolioCompanies, setPortfolioCompanies] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/portfolio`)
      .then((res) => {
        if (cancelled) return;
        setPortfolioCompanies(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // portfolioId (e.g. 'Exponent') is a loose slug, not the backend company_id —
  // resolve it the same way PortfolioPage does for its ?open= param: exact or
  // case-insensitive substring match against the company name.
  const findBackendCompany = (portfolioId) =>
    portfolioCompanies.find(
      (c) =>
        c.name === portfolioId ||
        (c.name && c.name.toLowerCase().includes(portfolioId.toLowerCase()))
    );

  const getLogoSrc = (company) => {
    const match = findBackendCompany(company.portfolioId);
    if (match?.logo_url) {
      return match.logo_url.startsWith('/') ? `${BACKEND_URL}${match.logo_url}` : match.logo_url;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(company.name)}&background=C8473A&color=fff&size=128`;
  };

  const getLogoAlt = (company) => {
    const match = findBackendCompany(company.portfolioId);
    return match?.logo_alt || `${company.name} logo`;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <main id="main-content" tabIndex={-1}>
        {/* Section 1 — Hero */}
        <section className="pt-32 pb-16 bg-white" aria-label="Responsible Investing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to About
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start"
            >
              <div className="lg:col-span-7">
                <p className="text-sm font-semibold text-[#C8473A] uppercase tracking-wider mb-4">
                  ESG at YourNest
                </p>
                <h1 className="text-4xl md:text-5xl font-bold text-[#211F1F] mb-6">
                  Responsible <span className="text-[#C8473A]">Investing</span>
                </h1>
                <p className="text-gray-700 leading-relaxed mb-4">
                  YourNest is committed to being a responsible investor, considering environmental, social and governance issues across all its investments by complying with the ESG policy, applicable laws of the land and being responsive to existing and emerging ESG concerns.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We adhere to our responsibility to people and the planet while setting the stage for long-term success. Through ESG integration in our investment approach, we endeavour to maximise business and investment opportunities for the benefit of all stakeholders.
                </p>
              </div>

              <div className="lg:col-span-5 flex flex-col gap-4">
                {HERO_FEATURE_CARDS.map((card) => (
                  <HeroFeatureCard key={card.title} card={card} />
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-3 gap-4 md:gap-8 mt-12 pt-10 border-t border-gray-100"
            >
              {HERO_STATS.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-[#C8473A]">{stat.value}</p>
                  <p className="text-xs md:text-sm font-medium text-gray-600 uppercase tracking-wider mt-1">
                    {stat.label}
                  </p>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12"
            >
              {ESG_PILLARS.map((pillar) => (
                <div
                  key={pillar.title}
                  className="bg-[#FAFAFA] rounded-2xl border border-gray-100 p-6"
                >
                  <div className="w-16 h-16 flex items-center justify-center mb-4">
                    <img
                      src={pillar.icon}
                      alt={pillar.iconAlt}
                      className="w-16 h-16 object-contain"
                    />
                  </div>
                  <h3 className="text-lg font-bold text-[#211F1F] mb-2">{pillar.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{pillar.description}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Sections 2+3 — Merged framework */}
        <section
          className="py-16 bg-[#FAFAFA]"
          aria-label="Responsible by Design and ESG integration framework"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#211F1F] mb-8">
              Responsible by <span className="text-[#C8473A]">Design,</span> not by Default
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-14 text-gray-700 leading-relaxed">
              <p>
                ESG is not an afterthought at YourNest — it is woven into how we source, evaluate, and support companies from day one. Our investment team applies structured ESG criteria at every stage, ensuring that responsible practices are a foundation for growth, not a checkbox.
              </p>
              <p>
                As of 2026 YourNest Venture Capital is a UN PRI signatory. The Principles for Responsible Investment (PRI) is a United Nations-backed global framework supported by many across the world. This deepens our ongoing commitment to embedding ESG considerations across every phase of our investment process.
              </p>
            </div>

            <h3 className="text-2xl md:text-3xl font-bold text-[#211F1F] mb-8">
              How we <span className="text-[#C8473A]">Integrate ESG</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {FRAMEWORK_PHASES.map((item, index) => (
                <motion.div
                  key={item.phase}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                  className="bg-white rounded-2xl border border-gray-100 border-t-4 border-t-[#C8473A] p-6 shadow-sm"
                >
                  <p className="text-sm font-semibold text-[#C8473A] mb-1">{item.phase}</p>
                  <h4 className="text-lg font-bold text-[#211F1F] mb-4">{item.title}</h4>
                  <ul className="space-y-2">
                    {item.bullets.map((bullet) => (
                      <li key={bullet} className="text-gray-600 text-sm leading-relaxed flex gap-2">
                        <span className="text-[#C8473A] flex-shrink-0" aria-hidden="true">
                          •
                        </span>
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 4 — UN PRI */}
        <section className="py-16 bg-white" aria-label="UN Principles for Responsible Investment">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#211F1F] mb-6">
                  UN Principles for <span className="text-[#C8473A]">Responsible Investment</span>
                </h2>
                <p className="text-gray-700 leading-relaxed mb-8">
                  As a signatory to the UN-supported Principles for Responsible Investment, YourNest commits to integrating ESG factors into investment decisions, active ownership, and transparent reporting — aligning our practices with a globally recognised framework for responsible investing.
                </p>
                <a
                  href="https://www.unpri.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block max-w-sm bg-[#FAFAFA] rounded-2xl border border-gray-100 overflow-hidden hover:border-[#C8473A]/30 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8473A] focus-visible:ring-offset-2"
                  aria-label="Signatory of PRI — visit unpri.org (opens in new tab)"
                >
                  <img
                    src="/images/pri-signatory-logo.png?v=20260601"
                    alt="PRI (Principles for Responsible Investment) signatory logo"
                    className="w-full h-auto block"
                    loading="lazy"
                  />
                </a>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 lg:pt-12">
                {UN_PRI_PRINCIPLES.map((principle, index) => (
                  <div
                    key={principle}
                    className="bg-[#FAFAFA] rounded-2xl border border-gray-100 p-5"
                  >
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#C8473A] text-white text-sm font-bold mb-3">
                      {index + 1}
                    </span>
                    <p className="text-gray-700 text-sm leading-relaxed">{principle}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section 5 — Portfolio spotlight */}
        <section className="py-16 bg-[#FAFAFA]" aria-label="Featured ESG companies in our Portfolio">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#211F1F] mb-10">
              Featured ESG companies <span className="text-[#C8473A]">in our Portfolio</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {PORTFOLIO_SPOTLIGHT.map((company, index) => (
                <motion.div
                  key={company.portfolioId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.06 }}
                  className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <Link
                    to={`/portfolio?open=${company.portfolioId}`}
                    className="flex items-center gap-4 mb-4 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8473A] focus-visible:ring-offset-2 rounded-xl"
                  >
                    <div className="w-16 h-16 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      <img
                        src={getLogoSrc(company)}
                        alt={getLogoAlt(company)}
                        className="w-full h-full object-contain p-1.5"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(company.name)}&background=C8473A&color=fff&size=128`;
                        }}
                      />
                    </div>
                    <h3 className="text-lg font-bold text-[#211F1F] group-hover:text-[#C8473A] transition-colors">
                      {company.name}
                    </h3>
                  </Link>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{company.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {company.sdgTags.map((tag) => (
                      <SdgTag key={tag} tag={tag} />
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="flex justify-center mt-10">
              <Button
                asChild
                variant="outline"
                className="border-2 border-[#211F1F] text-[#211F1F] hover:bg-[#211F1F] hover:text-white rounded-full px-8"
              >
                <Link to="/portfolio">View Portfolio</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Section 6 — SDG mapping */}
        <section className="py-16 bg-white" aria-label="SDG Mapping">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#211F1F] mb-10">
              <span className="text-[#C8473A]">SDG</span> Mapping
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-5">
              {SDG_TILES.map((tile) => (
                <SdgTile key={tile.number} tile={tile} />
              ))}
            </div>
          </div>
        </section>

        {/* Section 7 — Impact report & enquiries */}
        <section className="py-16 bg-[#FAFAFA]" aria-label="Annual Impact Report and ESG Enquiries">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
                <h2 className="text-2xl font-bold text-[#211F1F] mb-4">
                  Annual <span className="text-[#C8473A]">Impact Report</span>
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Read our latest impact report to learn how YourNest portfolio companies are driving measurable environmental and social outcomes across India.
                </p>
                <Button
                  asChild
                  className="bg-[#C8473A] hover:bg-[#B74F40] text-white rounded-full"
                >
                  <a
                    href="https://docsend.com/v/2ynfj/impact"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download Impact Report 2025 (opens in new tab)"
                  >
                    <Download className="w-4 h-4 mr-2" aria-hidden="true" />
                    Download Impact Report 2025
                  </a>
                </Button>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
                <h2 className="text-2xl font-bold text-[#211F1F] mb-4">
                  ESG <span className="text-[#C8473A]">Enquiries</span>
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  For questions about our ESG policy, PRI commitments, or portfolio impact data, please reach out to our ESG team.
                </p>
                <a
                  href="mailto:esg@yournest.in"
                  className="inline-flex items-center gap-2 text-[#C8473A] font-medium hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8473A] focus-visible:ring-offset-2 rounded"
                >
                  <Mail className="w-4 h-4" aria-hidden="true" />
                  esg@yournest.in
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default EsgPage;
